import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, EmptyState } from "@/components/common/StateBlocks";
import { marketingService } from "@/services/marketingService";
import { platformLabel, contentTypeLabel, statusBadge } from "@/config/marketingOptions";
import { fmtDate, fmtWeekday } from "@/lib/datetime";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const addMonths = (d, n) => { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; };
const startOfWeek = (d) => addDays(startOfDay(d), -new Date(d).getDay());
const sameDay = (a, b) => new Date(a).toDateString() === new Date(b).toDateString();
const isToday = (d) => sameDay(d, new Date());
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function ContentCalendar() {
  const navigate = useNavigate();
  const [view, setView] = useState(window.innerWidth < 768 ? "agenda" : "month");
  const [anchor, setAnchor] = useState(startOfDay(new Date()));
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const range = useMemo(() => {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const gridStart = startOfWeek(first);
    return { start: gridStart, end: addDays(gridStart, 42) };
  }, [anchor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = view === "agenda" ? startOfDay(new Date()).toISOString().slice(0, 10) : range.start.toISOString().slice(0, 10);
      const e = view === "agenda" ? addDays(new Date(), 30).toISOString().slice(0, 10) : range.end.toISOString().slice(0, 10);
      setItems((await marketingService.calendar({ start: s, end: e })).content);
    } catch { toast.error("Could not load content calendar"); }
    finally { setLoading(false); }
  }, [range, view]);
  useEffect(() => { load(); }, [load]);

  const forDay = (d) => items.filter((c) => sameDay(c.scheduled_for, d)).sort((a, b) => String(a.scheduled_for).localeCompare(String(b.scheduled_for)));
  const Chip = ({ c }) => (
    <button onClick={() => navigate(`/marketing/studio/${c.id}`)} data-testid={`cc-item-${c.id}`}
      className="block w-full truncate rounded border border-brand-navy/15 bg-brand-navy/5 px-1.5 py-0.5 text-left text-[11px] text-brand-navy hover:bg-brand-navy/10">
      {platformLabel(c.platform).slice(0, 3)} · {c.title}
    </button>
  );

  return (
    <AppShell>
      <div className="space-y-6" data-testid="content-calendar-page">
        <PageHeader eyebrow="AgentHQ · Marketing" title="Content Calendar"
          description="Plan your visibility before the week gets away from you."
          actions={<Button onClick={() => navigate("/marketing/studio")} data-testid="cc-create"><Plus className="h-4 w-4" /> Create Content</Button>} />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setAnchor(addMonths(anchor, -1))} data-testid="cc-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" onClick={() => setAnchor(startOfDay(new Date()))}>Today</Button>
            <Button variant="outline" size="sm" onClick={() => setAnchor(addMonths(anchor, 1))} data-testid="cc-next"><ChevronRight className="h-4 w-4" /></Button>
            <span className="ml-2 font-heading text-lg font-semibold">{view === "agenda" ? "Next 30 days" : anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</span>
          </div>
          <div className="flex gap-1 rounded-lg border border-border p-0.5">
            {["month", "week", "agenda"].map((v) => (
              <button key={v} onClick={() => setView(v)} data-testid={`cc-view-${v}`}
                className={`rounded-md px-3 py-1.5 text-sm font-medium capitalize ${view === v ? "bg-brand-navy text-white" : "text-muted-foreground hover:text-foreground"}`}>{v}</button>
            ))}
          </div>
        </div>

        {loading ? <div className="py-10"><LoadingState label="Loading calendar" /></div> : items.length === 0 ? (
          <EmptyState title="Plan your visibility before the week gets away from you." description="Add planned content to start building your calendar." />
        ) : view === "month" ? (
          <div className="overflow-hidden rounded-xl border border-border bg-card shadow-card" data-testid="cc-month">
            <div className="grid grid-cols-7 border-b border-border bg-secondary/50 text-center">
              {WEEKDAYS.map((d) => <div key={d} className="py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{d}</div>)}
            </div>
            <div className="grid grid-cols-7">
              {Array.from({ length: 42 }).map((_, i) => {
                const day = addDays(startOfWeek(new Date(anchor.getFullYear(), anchor.getMonth(), 1)), i);
                const inMonth = day.getMonth() === anchor.getMonth();
                const dayItems = forDay(day);
                return (
                  <div key={i} className={`min-h-[90px] border-b border-r border-border p-1.5 ${inMonth ? "" : "bg-secondary/20 text-muted-foreground"}`}>
                    <div className={`mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs ${isToday(day) ? "bg-brand-gold text-brand-navy" : ""}`}>{day.getDate()}</div>
                    <div className="space-y-0.5">{dayItems.slice(0, 3).map((c) => <Chip key={c.id} c={c} />)}{dayItems.length > 3 && <p className="px-1 text-[10px] text-muted-foreground">+{dayItems.length - 3}</p>}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : view === "week" ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-7" data-testid="cc-week">
            {Array.from({ length: 7 }).map((_, i) => {
              const day = addDays(startOfWeek(anchor), i);
              const dayItems = forDay(day);
              return (
                <div key={i} className="rounded-xl border border-border bg-card p-3 shadow-card">
                  <div className={`mb-2 text-sm font-semibold ${isToday(day) ? "text-accent" : ""}`}>{day.toLocaleDateString(undefined, { weekday: "short", day: "numeric" })}</div>
                  <div className="space-y-1">{dayItems.length === 0 ? <p className="text-xs text-muted-foreground">—</p> : dayItems.map((c) => <Chip key={c.id} c={c} />)}</div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-2" data-testid="cc-agenda">
            {items.map((c) => (
              <button key={c.id} onClick={() => navigate(`/marketing/studio/${c.id}`)} data-testid={`cc-item-${c.id}`}
                className="flex w-full items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-left shadow-card hover:bg-secondary">
                <div className="w-28 shrink-0 text-xs font-medium text-muted-foreground">{fmtWeekday(c.scheduled_for)}</div>
                <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium">{c.title}</p><p className="text-xs text-muted-foreground">{platformLabel(c.platform)} · {contentTypeLabel(c.content_type)}</p></div>
                <Badge className={`capitalize ${statusBadge(c.status)}`}>{c.status}</Badge>
              </button>
            ))}
          </div>
        )}
        <p className="text-xs text-muted-foreground">“Planned” means an intended date. No content is published to any social platform from AgentHQ.</p>
      </div>
    </AppShell>
  );
}
